import { Box } from '@mui/material';
import React, { FC, memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
	getActiveStep,
	getActiveSubStage,
	getActiveTicket,
	getIsStreaming,
	getIsSummaryLoading,
} from 'store/ticket-service/selector';

import { useEventBus } from 'utils/EventBus';

import {
	MessageActionStates,
	MessageActionTypes,
	SharePermissions,
	TicketStages,
} from 'core/enums/common';

import { Message } from 'components/Chatbot/Message/Message';
import { IMessage } from 'components/Chatbot/Message/types';

import { GenerateMessage } from './GenerateMessage';
import { RegenerateMessage } from './RegenerateMessage';

interface IMessageProps {
	model: IMessage;
}

export const SystemMessage: FC<IMessageProps> = memo(({ model }) => {
	const isStreaming = useSelector(getIsStreaming);
	const isSummaryLoading = useSelector(getIsSummaryLoading);
	const activeStep = useSelector(getActiveStep);
	const activeSubStage = useSelector(getActiveSubStage);
	const activeTicket = useSelector(getActiveTicket);

	const { dispatchEvent } = useEventBus();

	const handleGenerateAccept = () => {
		dispatchEvent('generateAccept', {});
	};

	const handleGenerateDecline = () => {
		dispatchEvent('generateDecline', {});
	};

	const handleRegenerateAccept = () => {
		dispatchEvent('regenerateAccept', {});
	};

	const handleErrorRegenerateAccept = () => {
		dispatchEvent('errorRegenerateAccept', {});
	};

	const isSystemButtonDisabled = useMemo(() => {
		if ((isStreaming && activeStep === TicketStages.general) || isSummaryLoading) return true;

		if (model?.action) {
			return (
				model.action.state === MessageActionStates.accept ||
				model.action.state === MessageActionStates.decline
			);
		}

		return false;
	}, [model, isStreaming, activeStep, isSummaryLoading]);

	const systemMessageHasButtons = useMemo(() => {
		return (
			model?.action &&
			model?.action?.type !== undefined &&
			model?.action?.type !== null &&
			model.action.type !== MessageActionTypes.used
		);
	}, [model?.action]);

	const isSharedWithReadPermission = useMemo(() => {
		if (activeTicket?.permissions?.length) {
			const currentPermission = activeTicket.permissions.find((permission) =>
				activeStep === TicketStages['general']
					? permission.ticketCurrentStep === TicketStages['general'] &&
					  (permission.subStageEnum === null || permission.subStageEnum === activeSubStage)
					: permission.ticketCurrentStep === activeStep
			);

			if (currentPermission) return currentPermission.permission === SharePermissions.Read;
		}

		return false;
	}, [activeTicket?.id, activeSubStage, activeStep]);

	const getSystemMessage = useCallback(() => {
		switch (model?.action?.type) {
			case MessageActionTypes.regenerate:
				return (
					<RegenerateMessage
						handleRegenerateAccept={handleRegenerateAccept}
						isDisabled={isSystemButtonDisabled || isSharedWithReadPermission}
					/>
				);
			case MessageActionTypes.generate:
				return (
					<GenerateMessage
						handleGenerateAccept={handleGenerateAccept}
						handleGenerateDecline={handleGenerateDecline}
						isDisabled={isSystemButtonDisabled || isSharedWithReadPermission}
					/>
				);
			case MessageActionTypes.error:
				return (
					<RegenerateMessage
						handleRegenerateAccept={handleErrorRegenerateAccept}
						isDisabled={isSystemButtonDisabled || isSharedWithReadPermission}
					/>
				);
			default:
				return null;
		}
	}, [model?.action, dispatchEvent, isSystemButtonDisabled]);

	return (
		<Box>
			<Message model={model} />
			{systemMessageHasButtons ? <>{getSystemMessage()}</> : null}
		</Box>
	);
});
